import { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Primitivas de interface do Seller.
//
// A repaginacao junta "metricas" e "saude" do mesmo objeto numa tela so. Para
// isso toda tela usa as mesmas pecas:
//   - SegmentedToggle  -> Tabela | Quadro (o kanban e um modo de ver, nao outra tela)
//   - Drawer           -> detalhe do item sem sair da lista (padrao Shopify/Amazon)
//   - OverflowMenu     -> acoes secundarias no "⋯", so a principal fica visivel
//   - FlowBoard        -> kanban por etapa de trabalho, com arrastar e soltar
// ---------------------------------------------------------------------------

// ---- Painel de automacoes (aberto por ?config=<aba> na URL) ----------------

interface SellerSettingsContextValue {
  openSettings: (tab?: string) => void;
  canConfigure: boolean;
}

export const SellerSettingsContext = createContext<SellerSettingsContextValue>({ openSettings: () => undefined, canConfigure: false });

export function useSellerSettings() {
  return useContext(SellerSettingsContext);
}

// ---- Drawer ----------------------------------------------------------------

export function Drawer({
  open, onClose, title, subtitle, children, width = 'max-w-2xl', footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  width?: string;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1200] flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className={`flex h-full w-full ${width} flex-col bg-white shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <div className="truncate text-base font-bold text-gray-900">{title}</div>
            {subtitle && <div className="mt-0.5 text-xs text-gray-500">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Fechar">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="border-t border-gray-100 px-5 py-3">{footer}</footer>}
      </aside>
    </div>
  );
}

// ---- Segmented toggle ------------------------------------------------------

export function SegmentedToggle<T extends string>({
  value, onChange, options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string; count?: number }>;
}) {
  return (
    <div className="inline-flex shrink-0 rounded-lg border border-gray-200 bg-white p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${value === opt.value ? 'bg-orange-700 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
        >
          {opt.label}
          {opt.count != null && <span className={`ml-1.5 ${value === opt.value ? 'text-white/70' : 'text-gray-400'}`}>{opt.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ---- Overflow menu ("⋯") ---------------------------------------------------

export interface OverflowItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  hint?: string;
}

export function OverflowMenu({ items, label = 'Mais ações' }: { items: OverflowItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (items.length === 0) return null;
  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={label}
        aria-label={label}
        className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2 py-1.5 text-gray-600 hover:bg-gray-50"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
      </button>
      {open && (
        <div className="absolute right-0 z-[1150] mt-1 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              disabled={item.disabled}
              onClick={() => { setOpen(false); item.onClick(); }}
              className={`block w-full px-3 py-2 text-left text-xs transition disabled:opacity-40 ${item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="font-medium">{item.label}</span>
              {item.hint && <span className="mt-0.5 block text-[10px] text-gray-400">{item.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Etapas de trabalho (kanban por fluxo) ---------------------------------

// O backend classifica por gravidade (critico, atencao...) e por revisao humana
// (pendente/revisado/corrigido/ignorado). O quadro mostra a etapa de trabalho;
// a gravidade vira etiqueta no card e ordena a coluna "Para revisar".
export type FlowStage = 'revisar' | 'revisando' | 'aguardando' | 'ok';

export const FLOW_STAGES: Array<{ id: FlowStage; label: string; help: string; dot: string; reviewStatus: string }> = [
  { id: 'revisar', label: 'Para revisar', help: 'A IA achou algo. Mais graves primeiro.', dot: 'bg-red-500', reviewStatus: 'pendente' },
  { id: 'revisando', label: 'Revisando', help: 'Você está mexendo nisso.', dot: 'bg-sky-500', reviewStatus: 'revisado' },
  { id: 'aguardando', label: 'Aguardando efeito', help: 'Corrigido ou coletando dados. Volta pra análise depois.', dot: 'bg-amber-400', reviewStatus: 'corrigido' },
  { id: 'ok', label: 'Tudo certo', help: 'Saudável ou ignorado.', dot: 'bg-emerald-500', reviewStatus: 'ignorado' },
];

export const URGENCY_ORDER: Record<string, number> = { urgente: 0, alta: 1, media: 2, baixa: 3, nenhuma: 4 };

export const URGENCY_BADGE: Record<string, { label: string; cls: string }> = {
  urgente: { label: 'Urgente', cls: 'bg-red-100 text-red-700' },
  alta: { label: 'Alta', cls: 'bg-orange-100 text-orange-700' },
  media: { label: 'Média', cls: 'bg-amber-100 text-amber-700' },
  baixa: { label: 'Baixa', cls: 'bg-sky-100 text-sky-700' },
  nenhuma: { label: 'OK', cls: 'bg-gray-100 text-gray-500' },
};

/**
 * Decide a etapa de um card a partir da revisao humana e da coluna de saude.
 * `waitingColumns` sao colunas de saude que significam "ainda nao da pra julgar"
 * (sem dados, aprendizado); `okColumns` sao as saudaveis.
 */
export function flowStageOf(
  card: { reviewStatus: string; column: string },
  okColumns: string[],
  waitingColumns: string[],
): FlowStage {
  if (card.reviewStatus === 'revisado') return 'revisando';
  if (card.reviewStatus === 'corrigido') return 'aguardando';
  if (card.reviewStatus === 'ignorado') return 'ok';
  if (okColumns.includes(card.column)) return 'ok';
  if (waitingColumns.includes(card.column)) return 'aguardando';
  return 'revisar';
}

export function FlowBoard<T extends { id: string; urgency: string }>({
  items, stageOf, renderCard, onMove, emptyHint,
}: {
  items: T[];
  stageOf: (item: T) => FlowStage;
  renderCard: (item: T) => ReactNode;
  /** Chamado ao soltar um card em outra coluna, com o reviewStatus da coluna. */
  onMove?: (item: T, stage: FlowStage, reviewStatus: string) => void;
  emptyHint?: string;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<FlowStage | null>(null);
  const [showAllOk, setShowAllOk] = useState(false);

  const grouped: Record<FlowStage, T[]> = { revisar: [], revisando: [], aguardando: [], ok: [] };
  for (const item of items) grouped[stageOf(item)].push(item);
  grouped.revisar.sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 9) - (URGENCY_ORDER[b.urgency] ?? 9));

  const drop = (stage: FlowStage) => {
    const item = items.find((i) => i.id === dragId);
    setDragId(null);
    setOverStage(null);
    if (!item || !onMove || stageOf(item) === stage) return;
    const def = FLOW_STAGES.find((s) => s.id === stage)!;
    onMove(item, stage, def.reviewStatus);
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      {FLOW_STAGES.map((stage) => {
        const cards = grouped[stage.id];
        // "Tudo certo" costuma ser a maior coluna e a menos util: mostra so o comeco.
        const visible = stage.id === 'ok' && !showAllOk ? cards.slice(0, 8) : cards;
        return (
          <div
            key={stage.id}
            onDragOver={(e) => { if (onMove) { e.preventDefault(); setOverStage(stage.id); } }}
            onDragLeave={() => setOverStage((s) => (s === stage.id ? null : s))}
            onDrop={() => drop(stage.id)}
            className={`flex min-w-0 flex-col rounded-xl p-2 transition ${overStage === stage.id ? 'bg-orange-50 ring-2 ring-orange-300' : 'bg-gray-50'}`}
          >
            <div className="mb-2 px-1.5 pt-1">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-bold text-gray-800">
                  <span className={`h-2 w-2 rounded-full ${stage.dot}`} />
                  {stage.label}
                </span>
                <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">{cards.length}</span>
              </div>
              <p className="mt-0.5 text-[10px] text-gray-400">{stage.help}</p>
            </div>
            <div className="flex flex-col gap-2">
              {visible.map((item) => (
                <div
                  key={item.id}
                  draggable={Boolean(onMove)}
                  onDragStart={() => setDragId(item.id)}
                  onDragEnd={() => { setDragId(null); setOverStage(null); }}
                  className={dragId === item.id ? 'opacity-40' : ''}
                >
                  {renderCard(item)}
                </div>
              ))}
              {cards.length === 0 && <p className="px-1 py-4 text-center text-[11px] text-gray-400">{stage.id === 'revisar' ? (emptyHint || 'Nada pendente') : 'vazio'}</p>}
              {stage.id === 'ok' && cards.length > 8 && (
                <button type="button" onClick={() => setShowAllOk((v) => !v)} className="py-1 text-[11px] font-medium text-gray-500 hover:text-gray-800">
                  {showAllOk ? 'Mostrar menos' : `Ver todos (${cards.length})`}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Pecas pequenas --------------------------------------------------------

export function KpiTile({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3.5">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 text-lg font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5">{sub}</div>}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
      <p className="text-sm font-medium text-gray-700">{title}</p>
      {children && <div className="mt-1 text-xs text-gray-500">{children}</div>}
    </div>
  );
}

export function marketplaceName(m?: string | null) {
  return m === 'mercadolivre' ? 'Mercado Livre' : 'Shopee';
}

// ---- Quadro por saúde (classificação da IA) ---------------------------------

// O quadro tem dois agrupamentos: por SAÚDE (o que a IA achou: crítico, atenção, saudável...)
// e por ETAPA de trabalho (para revisar → revisando → aguardando → tudo certo, com arrastar).
export type BoardGrouping = 'saude' | 'etapa';

export function useBoardGrouping(storageKey: string): [BoardGrouping, (g: BoardGrouping) => void] {
  const [value, setValue] = useState<BoardGrouping>(() => {
    try { return localStorage.getItem(storageKey) === 'etapa' ? 'etapa' : 'saude'; } catch { return 'saude'; }
  });
  const set = (g: BoardGrouping) => {
    setValue(g);
    try { localStorage.setItem(storageKey, g); } catch { /* sem storage: vale só nesta visita */ }
  };
  return [value, set];
}

export function BoardGroupingToggle({ value, onChange }: { value: BoardGrouping; onChange: (g: BoardGrouping) => void }) {
  return (
    <div className="flex items-center gap-2 text-[11px] text-gray-500">
      <span>Agrupar por</span>
      <SegmentedToggle<BoardGrouping>
        value={value}
        onChange={onChange}
        options={[{ value: 'saude', label: 'Saúde' }, { value: 'etapa', label: 'Etapa' }]}
      />
    </div>
  );
}

/** Etiqueta da revisão humana, para o card no quadro por saúde. */
export function ReviewTag({ reviewStatus, cooldownUntil }: { reviewStatus: string; cooldownUntil?: string | null }) {
  if (reviewStatus === 'revisado') return <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Revisando</span>;
  if (reviewStatus === 'corrigido') {
    const until = cooldownUntil && new Date(cooldownUntil) > new Date() ? ` até ${new Date(cooldownUntil).toLocaleDateString('pt-BR')}` : '';
    return <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">Corrigido · aguardando{until}</span>;
  }
  if (reviewStatus === 'ignorado') return <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">Ignorado</span>;
  return null;
}

export interface HealthColumn { id: string; label: string; dot: string; head: string }

export function HealthBoard<T extends { id: string; urgency: string; column: string }>({
  items, columns, renderCard,
}: {
  items: T[];
  columns: HealthColumn[];
  renderCard: (item: T) => ReactNode;
}) {
  const grouped = new Map<string, T[]>(columns.map(c => [c.id, []]));
  const fallback = columns[columns.length - 1].id;
  for (const item of items) (grouped.get(item.column) || grouped.get(fallback)!).push(item);
  for (const list of grouped.values()) list.sort((a, b) => (URGENCY_ORDER[a.urgency] ?? 9) - (URGENCY_ORDER[b.urgency] ?? 9));
  return (
    <div className={`grid grid-cols-1 gap-3 md:grid-cols-2 ${columns.length >= 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
      {columns.map(col => {
        const cards = grouped.get(col.id) || [];
        return (
          <div key={col.id} className="flex min-w-0 flex-col rounded-xl bg-gray-50 p-2">
            <div className={`mb-2 flex items-center justify-between rounded-lg border px-2.5 py-1.5 ${col.head}`}>
              <span className="flex items-center gap-1.5 text-xs font-bold"><span className={`h-2 w-2 rounded-full ${col.dot}`} />{col.label}</span>
              <span className="text-[10px] font-semibold">{cards.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {cards.map(item => <div key={item.id}>{renderCard(item)}</div>)}
              {cards.length === 0 && <p className="px-1 py-4 text-center text-[11px] text-gray-400">vazio</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
